import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../../../components/buttons/button";
import { Icon } from "../../../../../components/icons/icon";
import { ConfirmationModal } from "../../../../../components/modals/app_modals/confirmationModal";
import { ManageRoleModal } from "../../../../../components/modals/app_modals/manageRoleModal";
import { NotificationModal } from "../../../../../components/modals/app_modals/notificationModal";
import { SuccessModal } from "../../../../../components/modals/app_modals/successModal";
import { EmptyState } from "../../../../../components/states/emptyState";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import {
  useDeleteCooperativeRoleMutation,
  useGetCooperativeRolesQuery,
} from "../../../../../redux/features/settings/settings.api";
import { SingleRole } from "../../../../../redux/features/settings/settings.type";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../../../utils/constants";
import { BaseErrorResponse } from "../../../../../utils/types";

export const RolesSettings = () => {
  const [selectedRole, setSelectedRole] = useState<SingleRole>();
  const [createdRole, setCreatedRole] = useState<SingleRole>();

  const [searchParams, setSearchParams] = useSearchParams();

  const { modal, handleModalClose, handleModalOpen, modalOpen } =
    useModalControl();

  const navigate = useNavigate();

  const [deleteRole, { isLoading: deletingRole }] =
    useDeleteCooperativeRoleMutation();

  const {
    data: roles,
    isFetching: fetchingRoles,
    refetch: refetchRoles,
  } = useGetCooperativeRolesQuery(
    {
      limit: 100,
      offset: 0,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  return (
    <>
      <div className="flex flex-col gap-2">
        {fetchingRoles
          ? [1, 2, 3].map((i) => (
              <div
                className="py-3.5 w-full px-4 border border-border-grey-3 bg-light-green-bg rounded-lg overflow-hidden flex items-center gap-2"
                key={`${i}`}
              >
                <Skeleton
                  width={"100%"}
                  height={"24px"}
                  baseColor={SKELETON_BASE_COLOR}
                  highlightColor={SKELETON_HIGHLIGHT_COLOR}
                  className="max-w-[300px]"
                  containerClassName="flex-1"
                />
                <Skeleton
                  width={"32px"}
                  height={"32px"}
                  baseColor={SKELETON_BASE_COLOR}
                  highlightColor={SKELETON_HIGHLIGHT_COLOR}
                  className="rounded-full"
                />
                <Skeleton
                  width={"32px"}
                  height={"32px"}
                  baseColor={SKELETON_BASE_COLOR}
                  highlightColor={SKELETON_HIGHLIGHT_COLOR}
                  className="rounded-full"
                />
              </div>
            ))
          : undefined}

        {!fetchingRoles && [0, undefined].includes(roles?.data?.count) ? (
          <EmptyState
            image={
              <img
                src={"/assets/images/empty_state_roles.png"}
                alt="success"
                className=""
              />
            }
            imageClassName="!size-[204px]"
            titleClassName="max-w-[267px]"
            title={"Cooperative Roles"}
            subtitle="Create the different roles that will operate your cooperative. Assign to each role different accesses"
            actionText={"Create a role"}
            actionButtonVariant="primary"
            onClick={() => {
              handleModalOpen("manageRoleModal");
            }}
            className=""
          />
        ) : undefined}

        {!fetchingRoles && ![0, undefined].includes(roles?.data?.count)
          ? roles?.data?.results?.map((role) => {
              return (
                <div
                  key={role.id}
                  className="py-3.5 px-4 flex items-center gap-2 border border-border-grey-3 bg-light-green-bg rounded-lg"
                >
                  <p className="flex-1 font-medium text-pry-dark-green-300">
                    {role.Name}
                  </p>

                  <button
                    type="button"
                    className="p-1.5 rounded-full bg-light-red text-red-200"
                    onClick={() => {
                      if (roles.data.results.length > 2) {
                        setSelectedRole(role);
                        handleModalOpen("deleteRoleModal");
                      } else {
                        handleModalOpen("deleteWarningModal");
                      }
                    }}
                  >
                    <Icon svg="delete-icon" width={20} height={20} />
                  </button>

                  <button
                    type="button"
                    className="p-1.5 rounded-full bg-[#D1D9D7] text-pry-dark-green-300"
                    onClick={() => {
                      setSelectedRole(role);
                      handleModalOpen("manageRoleModal");
                    }}
                  >
                    <Icon svg="edit" width={20} height={20} />
                  </button>
                </div>
              );
            })
          : undefined}

        {!fetchingRoles && ![0, undefined].includes(roles?.data?.count) ? (
          <Button
            text="Add Role"
            icon="plus"
            className="!rounded-full !w-[136px] !ml-auto !mt-4"
            onClick={() => {
              setSelectedRole(undefined);
              handleModalOpen("manageRoleModal");
            }}
          />
        ) : undefined}
      </div>

      <ManageRoleModal
        isModalOpen={modal === "manageRoleModal" && modalOpen}
        onModalClose={() => {
          setSelectedRole(undefined);
          handleModalClose();
        }}
        successCallback={(created) => {
          refetchRoles();

          if (!selectedRole) {
            setCreatedRole(created);
            handleModalOpen("createRoleSuccessModal");
          }

          setSelectedRole(undefined);
        }}
        role={selectedRole}
      />

      <SuccessModal
        isModalOpen={modalOpen && modal === "createRoleSuccessModal"}
        onModalClose={() => {
          handleModalClose();
          setCreatedRole(undefined);
        }}
        message={"Role created successfully"}
        subText={
          <span>
            You have created the role of{" "}
            <span className="font-bold text-pry-dark-green-300/80">
              {createdRole?.Name}
            </span>
            . You can update this role whenever there’s a need to.
          </span>
        }
      >
        <div className="flex flex-col gap-3 mt-8 w-full">
          <Button
            type="button"
            variation="primary"
            onClick={() => {
              navigate("/app/settings?tab=manage_officials");
            }}
            text="Assign Official"
            className=""
          />

          <Button
            type="button"
            variation="plain"
            onClick={() => {
              handleModalClose();
              setCreatedRole(undefined);
              setSelectedRole(undefined);
            }}
            text="Dismiss"
            className=""
          />
        </div>
      </SuccessModal>

      <ConfirmationModal
        isModalOpen={modal === "deleteRoleModal" && modalOpen}
        onModalClose={() => {
          setSelectedRole(undefined);
          handleModalClose();
        }}
        title="Delete Role"
        subtitle={
          <span>
            You are about to delete the role of{" "}
            <span className="text-pry-green-200">{selectedRole?.Name}</span>.
          </span>
        }
        proceedButtonText="Yes, Delete"
        proceedButtonVariant="danger"
        cancelButtonText="Cancel"
        cancelButtonVariant="secondary-light-2"
        onProceed={async () => {
          const resp = await deleteRole({
            coop: selectedRole?.Cooperative!,
            roleId: selectedRole?.id!,
          });

          if (resp.data?.success) {
            handleModalClose();
            refetchRoles();
          } else if (
            (resp?.error as BaseErrorResponse).data.MsgCode ===
            "Cooperative.ApprovalLevel.less_than2.role"
          ) {
            handleModalOpen("affectedApprovalChainWarningModal");
          }
        }}
        loading={deletingRole}
      >
        <ul className="list-outside list-disc pl-5 text-sm">
          <li className="">
            If this role is part of any <b>approval chain</b>, it will be
            automatically <b>removed</b> from those chains.
          </li>
          <li>
            Any <b>member</b> currently assigned to this role <b>will lose</b>{" "}
            the associated permissions.
          </li>
          <li>
            The role will be permanently <b>removed</b> from the system.
          </li>
          <li>
            You can <b>recreate</b> the role later if needed.
          </li>
        </ul>
      </ConfirmationModal>

      <NotificationModal
        isModalOpen={modal === "affectedApprovalChainWarningModal" && modalOpen}
        onModalClose={() => {
          setSelectedRole(undefined);
          handleModalClose();
        }}
        title={
          <>
            <span className="text-4xl">⚠️</span>
            <br />
            Sorry, we can’t delete the{" "}
            <span className="text-pry-green-200">
              {selectedRole?.Name}
            </span>{" "}
            role just yet
          </>
        }
        subText={
          <>
            It is currently part of an approval chain that requires{" "}
            <span className="text-pry-green-200 font-medium">
              at least two approvers.
            </span>{" "}
            Deleting it now would reduce the chain to one approver, which
            violates system rules.
            <br />
            Please replace this role in the approval chain before trying to
            delete it again.
          </>
        }
      >
        <Button
          type="button"
          text="Edit approval chains"
          onClick={() => {
            handleModalClose();
            setSearchParams({
              ...Object.fromEntries(searchParams),
              view: "approvals",
            });
          }}
          variation="secondary-light-2"
        />
      </NotificationModal>

      <NotificationModal
        isModalOpen={modal === "deleteWarningModal" && modalOpen}
        onModalClose={() => {
          setSelectedRole(undefined);
          handleModalClose();
        }}
        title={
          <>
            <span className="text-4xl">⚠️</span>
            <br />
            Sorry, we can’t delete this role
          </>
        }
        subText={
          <>
            Your cooperative needs a{" "}
            <span className="text-pry-green-200 font-medium">minimum of 2</span>{" "}
            active roles to operate on Thriftcorp.
          </>
        }
      >
        <Button
          type="button"
          text="I understand"
          onClick={() => {
            handleModalClose();
          }}
          variation="secondary-light-2"
        />
      </NotificationModal>
    </>
  );
};
